import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScenarioSummary } from '../types';
import { ScenarioStatusBadge } from './ScenarioStatusBadge';
import { PermissionGate } from '../../admin/components/PermissionGate';
import { Permissions, usePermissions } from '../../admin/hooks/usePermissions';
import { AssignLearnersModal } from '../../admin/components/AssignLearnersModal';
import { TenantAvailabilityModal } from '../../admin/components/TenantAvailabilityModal';
import { showConfirm, showToast } from '../../../utils/alerts';
import { useTranslation } from '../../../i18n';

interface ScenarioTableProps {
  scenarios: ScenarioSummary[];
  loading?: boolean;
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
  search?: string;
  onSearchChange?: (newSearch: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (newStatus: string) => void;
  categoryFilter?: string;
  onCategoryFilterChange?: (newCategory: string) => void;
  difficultyFilter?: string;
  onDifficultyFilterChange?: (newDifficulty: string) => void;
  onPageChange?: (newPage: number) => void;
  onLimitChange?: (newLimit: number) => void;
  onPublish: (id: string) => Promise<void>;
  onUnpublish: (id: string) => Promise<void>;
  onDuplicate?: (id: string) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
}

export const ScenarioTable: React.FC<ScenarioTableProps> = ({
  scenarios,
  loading = false,
  page: controlledPage,
  limit: controlledLimit,
  total: controlledTotal,
  totalPages: controlledTotalPages,
  search: controlledSearch,
  onSearchChange,
  statusFilter: controlledStatusFilter,
  onStatusFilterChange,
  categoryFilter: controlledCategoryFilter,
  onCategoryFilterChange,
  difficultyFilter: controlledDifficultyFilter,
  onDifficultyFilterChange,
  onPageChange,
  onLimitChange,
  onPublish,
  onUnpublish,
  onDuplicate,
  onArchive,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPlatformAdmin, canAssign } = usePermissions();

  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [localStatusFilter, setLocalStatusFilter] = useState('ALL');
  const [localCategoryFilter, setLocalCategoryFilter] = useState('ALL');
  const [localDifficultyFilter, setLocalDifficultyFilter] = useState('ALL');

  const searchTerm = controlledSearch !== undefined ? controlledSearch : localSearchTerm;
  const statusFilter = controlledStatusFilter !== undefined ? controlledStatusFilter : localStatusFilter;
  const categoryFilter = controlledCategoryFilter !== undefined ? controlledCategoryFilter : localCategoryFilter;
  const difficultyFilter = controlledDifficultyFilter !== undefined ? controlledDifficultyFilter : localDifficultyFilter;

  const [sortField, setSortField] = useState<'title' | 'category' | 'difficulty' | 'status' | 'timeLimitMinutes' | 'createdAt'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return 'grid';
    }
    return 'table';
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768 && viewMode === 'table') {
        setViewMode('grid');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  const [processingId, setProcessingId] = useState<string | null>(null);

  // Local pagination state for fallback / un-paginated parent
  const [localPage, setLocalPage] = useState(1);
  const [localLimit, setLocalLimit] = useState(20);

  const [assignScenario, setAssignScenario] = useState<ScenarioSummary | null>(null);
  const [accessScenario, setAccessScenario] = useState<ScenarioSummary | null>(null);

  const categories = useMemo(() => {
    return Array.from(new Set(scenarios.map((s) => s.category).filter(Boolean)));
  }, [scenarios]);

  const isServerPaginated = Boolean(controlledPage && onPageChange);

  // Client-side filtering and sorting for fallback or in-memory
  const filteredAndSorted = useMemo(() => {
    let result = onSearchChange
      ? scenarios.filter((s) => {
          if (searchTerm.trim() && statusFilter !== 'ARCHIVED' && (s.status || '').toUpperCase() === 'ARCHIVED') {
            return false;
          }
          return true;
        })
      : scenarios.filter((s) => {
          if (searchTerm.trim() && statusFilter !== 'ARCHIVED' && (s.status || '').toUpperCase() === 'ARCHIVED') {
            return false;
          }
          if (statusFilter !== 'ALL' && s.status !== statusFilter) return false;
          if (categoryFilter !== 'ALL' && s.category !== categoryFilter) return false;
          if (difficultyFilter !== 'ALL' && (s.difficulty || '').toUpperCase() !== difficultyFilter) return false;
          if (searchTerm.trim()) {
        const rawQ = searchTerm.toLowerCase().trim();
        const normalizeText = (txt: string) =>
          (txt || '')
            .toLowerCase()
            .replace(/[\u2013\u2014\-]/g, ' ')
            .replace(/[&]/g, ' and ')
            .replace(/[^a-z0-9\s]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        const normalizedQ = normalizeText(rawQ);
        const searchTokens = normalizedQ.split(/\s+/).filter((t: string) => t.length > 0);

        const fullScenarioText = normalizeText(
          `${s.title || ''} ${s.description || ''} ${s.category || ''} ${s.targetPersona || ''} ${s.targetPersonaRole || ''} ${s.learnerRole || ''} ${(s as any).persona?.name || ''} ${(s as any).persona?.role || ''}`
        );

        if (fullScenarioText.includes(normalizedQ)) return true;
        if (searchTokens.length > 0 && searchTokens.every((token: string) => fullScenarioText.includes(token))) return true;

        const rawScenarioText = `${s.title || ''} ${s.description || ''} ${s.category || ''} ${s.targetPersona || ''} ${s.targetPersonaRole || ''} ${s.learnerRole || ''}`.toLowerCase();
        if (!rawScenarioText.includes(rawQ)) {
          return false;
        }
      }
      return true;
    });

    result.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '');
      } else if (sortField === 'category') {
        comparison = (a.category || '').localeCompare(b.category || '');
      } else if (sortField === 'difficulty') {
        comparison = (a.difficulty || '').localeCompare(b.difficulty || '');
      } else if (sortField === 'status') {
        comparison = (a.status || '').localeCompare(b.status || '');
      } else if (sortField === 'timeLimitMinutes') {
        comparison = (a.timeLimitMinutes || 0) - (b.timeLimitMinutes || 0);
      } else {
        comparison = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [scenarios, onSearchChange, statusFilter, categoryFilter, difficultyFilter, searchTerm, sortField, sortOrder]);

  // Determine pagination variables
  const activePage = isServerPaginated ? (controlledPage || 1) : localPage;
  const activeLimit = isServerPaginated ? (controlledLimit || 20) : localLimit;
  const totalItems = isServerPaginated ? (controlledTotal ?? filteredAndSorted.length) : filteredAndSorted.length;
  const totalPages = isServerPaginated
    ? (controlledTotalPages ?? Math.max(1, Math.ceil(totalItems / activeLimit)))
    : Math.max(1, Math.ceil(totalItems / activeLimit));

  // Current slice for local display
  const displayedScenarios = isServerPaginated
    ? filteredAndSorted
    : filteredAndSorted.slice((activePage - 1) * activeLimit, activePage * activeLimit);

  const handlePageChange = (newPage: number) => {
    const clampedPage = Math.max(1, Math.min(newPage, totalPages));
    if (isServerPaginated && onPageChange) {
      onPageChange(clampedPage);
    } else {
      setLocalPage(clampedPage);
    }
  };

  const handleSearchInput = (val: string) => {
    if (onSearchChange) {
      onSearchChange(val);
      if (onPageChange) onPageChange(1);
    } else {
      setLocalSearchTerm(val);
      setLocalPage(1);
    }
  };

  const handleStatusChange = (val: string) => {
    if (onStatusFilterChange) {
      onStatusFilterChange(val);
      if (onPageChange) onPageChange(1);
    } else {
      setLocalStatusFilter(val);
      setLocalPage(1);
    }
  };

  const handleCategoryChange = (val: string) => {
    if (onCategoryFilterChange) {
      onCategoryFilterChange(val);
      if (onPageChange) onPageChange(1);
    } else {
      setLocalCategoryFilter(val);
      setLocalPage(1);
    }
  };

  const handleDifficultyChange = (val: string) => {
    if (onDifficultyFilterChange) {
      onDifficultyFilterChange(val);
      if (onPageChange) onPageChange(1);
    } else {
      setLocalDifficultyFilter(val);
      setLocalPage(1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    if (isServerPaginated && onLimitChange) {
      onLimitChange(newLimit);
    } else {
      setLocalLimit(newLimit);
      setLocalPage(1);
    }
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleAction = async (action: () => Promise<void>, id: string) => {
    setProcessingId(id);
    try {
      await action();
    } finally {
      setProcessingId(null);
    }
  };

  const startItem = totalItems === 0 ? 0 : (activePage - 1) * activeLimit + 1;
  const endItem = Math.min(activePage * activeLimit, totalItems);

  // Generate pagination pill array
  const getPaginationPills = () => {
    const pills: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pills.push(i);
    } else {
      pills.push(1);
      if (activePage > 3) pills.push('...');
      const start = Math.max(2, activePage - 1);
      const end = Math.min(totalPages - 1, activePage + 1);
      for (let i = start; i <= end; i++) pills.push(i);
      if (activePage < totalPages - 2) pills.push('...');
      pills.push(totalPages);
    }
    return pills;
  };

  return (
    <div style={{ display: 'grid', gap: '0px' }}>
      {/* Search, Filter & View Controls */}
      {/* Search & Filter Toolbar */}
      <div className="scenario-table__toolbar">
        <div className="scenario-table__search">
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              className="admin-form__input"
              placeholder="Search by title, role, persona, category..."
              value={searchTerm}
              onChange={(e) => handleSearchInput(e.target.value)}
              style={{
                width: '100%',
                height: '38px',
                paddingLeft: '36px',
                paddingRight: '28px',
                borderRadius: '8px',
                border: '1px solid var(--line)',
                fontSize: '13px',
                background: '#fafbfc',
              }}
            />
            <svg
              style={{ position: 'absolute', left: '11px', top: '11px', width: '15px', height: '15px', color: 'var(--faint)' }}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {searchTerm && (
              <button
                type="button"
                onClick={() => handleSearchInput('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '10px',
                  background: 'none',
                  border: 'none',
                  color: 'var(--faint)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: 0,
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="scenario-table__filters">
          {/* Status Filter */}
          <div className="scenario-table__filter-item">
            <select
              className="scenario-table__filter-select"
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              <option value="ALL">{t('table.filter_all_statuses', undefined, 'All Statuses')}</option>
              <option value="PUBLISHED">{t('table.published', undefined, 'Published')}</option>
              <option value="DRAFT">{t('table.draft', undefined, 'Draft')}</option>
              <option value="ARCHIVED">{t('table.archived', undefined, 'Archived')}</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="scenario-table__filter-item">
            <select
              className="scenario-table__filter-select"
              value={categoryFilter}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="ALL">{t('table.filter_all_categories', undefined, 'All Categories')}</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty Filter */}
          <div className="scenario-table__filter-item">
            <select
              className="scenario-table__filter-select"
              value={difficultyFilter}
              onChange={(e) => handleDifficultyChange(e.target.value)}
            >
              <option value="ALL">{t('table.filter_all_difficulties', undefined, 'All Difficulties')}</option>
              <option value="BEGINNER">{t('table.beginner', undefined, 'Beginner')}</option>
              <option value="INTERMEDIATE">{t('table.intermediate', undefined, 'Intermediate')}</option>
              <option value="ADVANCED">{t('table.advanced', undefined, 'Advanced')}</option>
            </select>
          </div>

          {/* View Mode Toggle: Table View vs Grid View */}
          <div className="scenario-table__view-toggle">
            <button
              type="button"
              aria-pressed={viewMode === 'table'}
              onClick={() => setViewMode('table')}
              title="Data Table View"
              style={{
                width: '36px',
                height: '36px',
                border: 0,
                background: viewMode === 'table' ? 'var(--blue-soft, #eff6ff)' : 'transparent',
                color: viewMode === 'table' ? 'var(--blue, #2563eb)' : 'var(--faint, #94a3b8)',
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'grid'}
              onClick={() => setViewMode('grid')}
              title="Card Grid View"
              style={{
                width: '36px',
                height: '36px',
                border: 0,
                background: viewMode === 'grid' ? 'var(--blue-soft, #eff6ff)' : 'transparent',
                color: viewMode === 'grid' ? 'var(--blue, #2563eb)' : 'var(--faint, #94a3b8)',
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content: Data Table or Grid View */}
      {viewMode === 'table' ? (
        <div className="admin-table-container" style={{ overflowX: 'auto' }}>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('title')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by Title"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{t('table.scenario', undefined, 'Scenario')}</span>
                    {sortField === 'title' && (
                      <span style={{ fontSize: '11px', color: 'var(--blue)' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('category')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by Category"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{t('table.category', undefined, 'Category')}</span>
                    {sortField === 'category' && (
                      <span style={{ fontSize: '11px', color: 'var(--blue)' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th>{t('table.ai_persona', undefined, 'AI Persona')}</th>
                <th
                  onClick={() => handleSort('difficulty')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by Difficulty"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{t('table.difficulty', undefined, 'Difficulty')}</span>
                    {sortField === 'difficulty' && (
                      <span style={{ fontSize: '11px', color: 'var(--blue)' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('status')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by Status"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{t('table.status', undefined, 'Status')}</span>
                    {sortField === 'status' && (
                      <span style={{ fontSize: '11px', color: 'var(--blue)' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('timeLimitMinutes')}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  title="Sort by Duration"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>{t('table.duration', undefined, 'Duration')}</span>
                    {sortField === 'timeLimitMinutes' && (
                      <span style={{ fontSize: '11px', color: 'var(--blue)' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th style={{ textAlign: 'right' }}>{t('table.actions', undefined, 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
                    <div className="dashboard-spinner" style={{ margin: '0 auto 10px', width: '24px', height: '24px' }} />
                    Loading simulation scenarios...
                  </td>
                </tr>
              ) : displayedScenarios.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
                    <div style={{ fontSize: '28px', marginBottom: '8px' }}>📂</div>
                    <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: '15px' }}>No scenarios found</div>
                    <div style={{ fontSize: '13px', marginTop: '4px' }}>
                      {searchTerm ? 'Try adjusting your search criteria or filters.' : 'No simulation scenarios available.'}
                    </div>
                  </td>
                </tr>
              ) : (
                displayedScenarios.map((s) => (
                  <tr key={s.id} style={{ transition: 'background 0.15s ease' }}>
                    <td style={{ maxWidth: '340px' }}>
                      <div
                        style={{ fontWeight: 600, color: 'var(--navy)', cursor: 'pointer', fontSize: '13.5px' }}
                        onClick={() => navigate(`/scenarios/${s.id}`)}
                        title={s.title}
                      >
                        {s.title}
                      </div>
                      {s.description && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--muted)',
                            marginTop: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={s.description}
                        >
                          {s.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: '12px',
                          background: 'var(--surface, #f8fafc)',
                          padding: '3px 8px',
                          borderRadius: '5px',
                          border: '1px solid var(--line, #e2e8f0)',
                          color: 'var(--ink, #1e293b)',
                          fontWeight: 500,
                        }}
                      >
                        {s.category || 'General'}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>
                        {s.targetPersona || 'AI Persona'}
                      </div>
                      <div style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                        {s.targetPersonaRole || 'Customer'}
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color:
                            s.difficulty === 'ADVANCED'
                              ? '#dc2626'
                              : s.difficulty === 'BEGINNER'
                              ? '#16a34a'
                              : 'var(--muted, #64748b)',
                          textTransform: 'capitalize',
                        }}
                      >
                        {s.difficulty?.toLowerCase() || 'intermediate'}
                      </span>
                    </td>
                    <td>
                      <ScenarioStatusBadge status={s.status} />
                    </td>
                    <td>
                      <span style={{ fontFamily: 'monospace', fontSize: '12.5px', color: 'var(--ink)' }}>
                        {s.timeLimitMinutes || 10}m
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div
                        className="admin-table-actions"
                        style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
                          <button
                            type="button"
                            onClick={() => navigate(`/scenarios/${s.id}/edit`)}
                            className="admin-btn admin-btn--secondary admin-btn--sm"
                            style={{ height: '30px', padding: '0 10px', fontSize: '12px', borderRadius: '6px', fontWeight: 600 }}
                          >
                            Edit
                          </button>
                        </PermissionGate>

                        <PermissionGate permission={Permissions.SCENARIO_PUBLISH}>
                          {s.status === 'PUBLISHED' ? (
                            <button
                              type="button"
                              disabled={processingId === s.id}
                              onClick={() => handleAction(() => onUnpublish(s.id), s.id)}
                              className="admin-btn admin-btn--warning admin-btn--sm"
                              style={{ height: '30px', padding: '0 10px', fontSize: '12px', borderRadius: '6px', fontWeight: 600 }}
                              title="Revert to Draft"
                            >
                              Unpublish
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={processingId === s.id}
                              onClick={() => handleAction(() => onPublish(s.id), s.id)}
                              className="admin-btn admin-btn--primary admin-btn--sm"
                              style={{ height: '30px', padding: '0 10px', fontSize: '12px', borderRadius: '6px', fontWeight: 600 }}
                              title="Publish Scenario"
                            >
                              Publish
                            </button>
                          )}
                        </PermissionGate>

                        {/* Assign Learners Modal trigger (Admin & Super Admin only) */}
                        {canAssign && (
                          <button
                            type="button"
                            onClick={() => setAssignScenario(s)}
                            className="admin-btn admin-btn--secondary admin-btn--sm"
                            style={{
                              height: '30px',
                              padding: '0 10px',
                              fontSize: '12px',
                              borderRadius: '6px',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                            title="Assign simulation to learners"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <line x1="19" y1="8" x2="19" y2="14" />
                              <line x1="22" y1="11" x2="16" y2="11" />
                            </svg>
                            <span>Assign</span>
                          </button>
                        )}

                        {/* Super Admin Tenant Whitelist / Access */}
                        {isPlatformAdmin && (
                          <button
                            type="button"
                            onClick={() => setAccessScenario(s)}
                            className="admin-btn admin-btn--secondary admin-btn--sm"
                            style={{
                              width: '30px',
                              height: '30px',
                              padding: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                            }}
                            title="Manage Organization Whitelist / Access"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                            </svg>
                          </button>
                        )}

                        <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
                          <button
                            type="button"
                            onClick={() => navigate(`/scenarios/${s.id}/evaluation-studio`)}
                            className="admin-btn admin-btn--secondary admin-btn--sm"
                            style={{
                              width: '30px',
                              height: '30px',
                              padding: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                            }}
                            title="Configure Evaluation Studio & Prompts"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="3" />
                              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                            </svg>
                          </button>

                          {onDuplicate && (
                            <button
                              type="button"
                              disabled={processingId === s.id}
                              onClick={() => handleAction(() => onDuplicate(s.id), s.id)}
                              className="admin-btn admin-btn--secondary admin-btn--sm"
                              style={{
                                width: '30px',
                                height: '30px',
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px',
                              }}
                              title="Duplicate Scenario Blueprint"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                            </button>
                          )}
                        </PermissionGate>

                        <PermissionGate permission={Permissions.SCENARIO_CREATE_EDIT}>
                          {s.status !== 'ARCHIVED' && (
                            <button
                              type="button"
                              disabled={processingId === s.id}
                              onClick={async () => {
                                const confirmed = await showConfirm({
                                  title: 'Archive Scenario?',
                                  text: `Are you sure you want to archive "${s.title}"?`,
                                  confirmText: 'Archive Scenario',
                                  cancelText: 'Cancel',
                                  icon: 'warning',
                                  isDestructive: true,
                                });
                                if (confirmed) {
                                  handleAction(() => onArchive(s.id), s.id);
                                }
                              }}
                              className="admin-btn admin-btn--danger admin-btn--sm"
                              style={{
                                width: '30px',
                                height: '30px',
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px',
                              }}
                              title="Archive Scenario"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          )}
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Grid Card View */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px', padding: '16px' }}>
          {loading ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
              <div className="dashboard-spinner" style={{ margin: '0 auto 10px', width: '24px', height: '24px' }} />
              Loading simulation scenarios...
            </div>
          ) : displayedScenarios.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px 20px', color: 'var(--muted)' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>📂</div>
              <div style={{ fontWeight: 600, color: 'var(--ink)', fontSize: '15px' }}>No scenarios found</div>
              <div style={{ fontSize: '13px', marginTop: '4px' }}>
                {searchTerm ? 'Try adjusting your search criteria or filters.' : 'No simulation scenarios available.'}
              </div>
            </div>
          ) : (
            displayedScenarios.map((s) => (
              <div
                key={s.id}
                style={{
                  background: '#ffffff',
                  border: '1px solid var(--line)',
                  borderRadius: '12px',
                  padding: '18px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span
                    style={{
                      fontSize: '11px',
                      background: 'var(--blue-soft, #eff6ff)',
                      color: 'var(--blue, #2563eb)',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      fontWeight: 700,
                    }}
                  >
                    {s.category || 'General'}
                  </span>
                  <ScenarioStatusBadge status={s.status} />
                </div>

                <div>
                  <h3
                    onClick={() => navigate(`/scenarios/${s.id}`)}
                    style={{
                      fontFamily: 'var(--display)',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--navy)',
                      margin: '0 0 6px',
                      cursor: 'pointer',
                    }}
                  >
                    {s.title}
                  </h3>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '13px',
                      color: 'var(--muted)',
                      lineHeight: '1.4',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {s.description || 'No description provided.'}
                  </p>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    background: 'var(--surface, #f8fafc)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                >
                  <div>
                    <div style={{ color: 'var(--faint)', fontSize: '10.5px', textTransform: 'uppercase', fontWeight: 700 }}>
                      Persona
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{s.targetPersona || 'AI Persona'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--faint)', fontSize: '10.5px', textTransform: 'uppercase', fontWeight: 700 }}>
                      Duration
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--ink)', fontFamily: 'monospace' }}>
                      {s.timeLimitMinutes || 10} mins
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '6px',
                    marginTop: 'auto',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--line)',
                  }}
                >
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => navigate(`/scenarios/${s.id}/edit`)}
                      className="admin-btn admin-btn--secondary admin-btn--sm"
                      style={{ height: '30px', padding: '0 10px', fontSize: '12px', borderRadius: '6px', fontWeight: 600 }}
                    >
                      Edit
                    </button>
                    {canAssign && (
                      <button
                        type="button"
                        onClick={() => setAssignScenario(s)}
                        className="admin-btn admin-btn--secondary admin-btn--sm"
                        style={{ height: '30px', padding: '0 10px', fontSize: '12px', borderRadius: '6px', fontWeight: 600 }}
                      >
                        Assign
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      type="button"
                      onClick={() => navigate(`/scenarios/${s.id}/evaluation-studio`)}
                      className="admin-btn admin-btn--secondary admin-btn--sm"
                      style={{ width: '30px', height: '30px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}
                      title="Evaluation Studio"
                    >
                      ⚙️
                    </button>
                    {s.status === 'PUBLISHED' ? (
                      <button
                        type="button"
                        disabled={processingId === s.id}
                        onClick={() => handleAction(() => onUnpublish(s.id), s.id)}
                        className="admin-btn admin-btn--warning admin-btn--sm"
                        style={{ height: '30px', padding: '0 10px', fontSize: '12px', borderRadius: '6px', fontWeight: 600 }}
                      >
                        Unpublish
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={processingId === s.id}
                        onClick={() => handleAction(() => onPublish(s.id), s.id)}
                        className="admin-btn admin-btn--primary admin-btn--sm"
                        style={{ height: '30px', padding: '0 10px', fontSize: '12px', borderRadius: '6px', fontWeight: 600 }}
                      >
                        Publish
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination Footer */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '12px 18px',
          borderTop: '1px solid var(--line)',
          background: 'var(--white)',
          fontSize: '13px',
          color: 'var(--muted)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>
            Showing <strong>{startItem}</strong>–<strong>{endItem}</strong> of <strong>{totalItems}</strong> scenarios
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '12px' }}>Per page:</span>
            <select
              value={activeLimit}
              onChange={(e) => handleLimitChange(Number(e.target.value))}
              style={{
                height: '30px',
                padding: '0 24px 0 8px',
                borderRadius: '6px',
                border: '1px solid var(--line)',
                background: '#ffffff url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M4 6l4 4 4-4\' fill=\'none\' stroke=\'%2364748b\' stroke-width=\'1.8\' stroke-linecap=\'round\'/%3E%3C/svg%3E") right 6px center/10px no-repeat',
                appearance: 'none',
                WebkitAppearance: 'none',
                fontSize: '12.5px',
                color: 'var(--ink)',
                cursor: 'pointer',
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Page Navigation Pills */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            type="button"
            disabled={activePage <= 1 || loading}
            onClick={() => handlePageChange(1)}
            style={{
              height: '32px',
              minWidth: '32px',
              padding: '0 8px',
              borderRadius: '6px',
              border: '1px solid var(--line)',
              background: activePage <= 1 ? '#f1f5f9' : '#ffffff',
              color: activePage <= 1 ? '#94a3b8' : 'var(--ink)',
              cursor: activePage <= 1 ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="First Page"
          >
            «
          </button>
          <button
            type="button"
            disabled={activePage <= 1 || loading}
            onClick={() => handlePageChange(activePage - 1)}
            style={{
              height: '32px',
              minWidth: '32px',
              padding: '0 8px',
              borderRadius: '6px',
              border: '1px solid var(--line)',
              background: activePage <= 1 ? '#f1f5f9' : '#ffffff',
              color: activePage <= 1 ? '#94a3b8' : 'var(--ink)',
              cursor: activePage <= 1 ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Previous Page"
          >
            ‹
          </button>

          {getPaginationPills().map((pill, idx) =>
            typeof pill === 'number' ? (
              <button
                key={idx}
                type="button"
                disabled={loading}
                onClick={() => handlePageChange(pill)}
                style={{
                  height: '32px',
                  minWidth: '32px',
                  padding: '0 8px',
                  borderRadius: '6px',
                  border: pill === activePage ? '1px solid var(--blue)' : '1px solid var(--line)',
                  background: pill === activePage ? 'var(--blue, #2563eb)' : '#ffffff',
                  color: pill === activePage ? '#ffffff' : 'var(--ink)',
                  cursor: 'pointer',
                  fontWeight: pill === activePage ? 700 : 500,
                  fontSize: '12.5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease',
                }}
              >
                {pill}
              </button>
            ) : (
              <span key={idx} style={{ padding: '0 4px', color: 'var(--faint)' }}>
                {pill}
              </span>
            )
          )}

          <button
            type="button"
            disabled={activePage >= totalPages || loading}
            onClick={() => handlePageChange(activePage + 1)}
            style={{
              height: '32px',
              minWidth: '32px',
              padding: '0 8px',
              borderRadius: '6px',
              border: '1px solid var(--line)',
              background: activePage >= totalPages ? '#f1f5f9' : '#ffffff',
              color: activePage >= totalPages ? '#94a3b8' : 'var(--ink)',
              cursor: activePage >= totalPages ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Next Page"
          >
            ›
          </button>
          <button
            type="button"
            disabled={activePage >= totalPages || loading}
            onClick={() => handlePageChange(totalPages)}
            style={{
              height: '32px',
              minWidth: '32px',
              padding: '0 8px',
              borderRadius: '6px',
              border: '1px solid var(--line)',
              background: activePage >= totalPages ? '#f1f5f9' : '#ffffff',
              color: activePage >= totalPages ? '#94a3b8' : 'var(--ink)',
              cursor: activePage >= totalPages ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Last Page"
          >
            »
          </button>
        </div>
      </div>

      {/* Modals */}
      {canAssign && assignScenario && (
        <AssignLearnersModal
          simulationId={assignScenario.id}
          simulationTitle={assignScenario.title}
          tenantId={assignScenario.tenantId}
          isOpen={Boolean(assignScenario)}
          onClose={() => setAssignScenario(null)}
          onAssigned={(count) => {
            setAssignScenario(null);
            showToast(
              `Assigned simulation to ${count} learner${count === 1 ? '' : 's'} successfully!`,
              'success'
            );
          }}
        />
      )}

      {accessScenario && (
        <TenantAvailabilityModal
          simulationId={accessScenario.id}
          simulationTitle={accessScenario.title}
          isOpen={Boolean(accessScenario)}
          onClose={() => setAccessScenario(null)}
        />
      )}
    </div>
  );
};
